import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { SEOHead } from '../components';
import {
  EigenLeadsHeroDiagram,
  EigenLeadsSupplyChainDiagram,
  EigenLeadsQualityDiagram,
  EigenLeadsDeliveryDiagram,
  EigenLeadsExecutiveBriefingPlayer,
} from '../components/executive';
import {
  ArrowLeft,
  ArrowRight,
  Shield,
  Target,
  Clock,
  CheckCircle2,
  Layers,
  FileCheck,
  TrendingUp,
  RefreshCw,
  Building2,
  BadgeCheck,
  Repeat,
  Zap,
} from 'lucide-react';

const supplyChainPillars = [
  {
    icon: BadgeCheck,
    title: 'Trust',
    description: 'Provenance on every record. Standards your leadership, compliance, and producers can stand behind.',
    accent: 'indigo',
  },
  {
    icon: Shield,
    title: 'Reliable',
    description: 'Operated programs with defined ownership and delivery cadence. Supply shows up when your territory needs it.',
    accent: 'emerald',
  },
  {
    icon: Repeat,
    title: 'Consistent',
    description: 'The same quality bar on every release. No swing between vendors, batches, or release cycles.',
    accent: 'amber',
  },
  {
    icon: Zap,
    title: 'Available',
    description: 'Ranked contacts in your queue on schedule — verified, prioritized, and ready for outreach.',
    accent: 'rose',
  },
];

const differentiators = [
  {
    icon: Target,
    label: 'EigenScore™',
    detail: 'Priority with visible rationale — not an unexplained “high-intent” label. Producers see who to call and why now.',
  },
  {
    icon: FileCheck,
    label: 'QualityCert™',
    detail: 'A release gate backed by cited scoring context, CAB evidence, and an auditable receipt — not quality by assertion.',
  },
  {
    icon: Layers,
    label: 'Diversified Supply Chain',
    detail: 'Multiple supply lanes under one governed CAB standard. Territory performance does not depend on one inventory source.',
  },
  {
    icon: Shield,
    label: 'Compliance In The Path',
    detail: 'DNC, TCPA, DUI, and SR-22 readiness travels with the opportunity before producer time is committed.',
  },
  {
    icon: Building2,
    label: 'Capacity-Aware Routing',
    detail: 'Territory ownership, equitable assignment, monthly caps, and queue utilization govern who receives the next opportunity.',
  },
  {
    icon: Zap,
    label: 'Producer-Native Execution',
    detail: 'Inbox-to-call resolution, one-tap disposition, and auto-advance remove duplicate lookup and administrative motion.',
  },
  {
    icon: TrendingUp,
    label: 'Source-To-Outcome Attribution',
    detail: 'Worked, reached, quoted, and bound outcomes remain connected to the producer, territory, source, signal, and CAB.',
  },
  {
    icon: RefreshCw,
    label: 'Spend Follows Proof',
    detail: 'Allocation follows observed conversion and CAB economics. Field outcomes improve the next acquisition decision.',
  },
  {
    icon: BadgeCheck,
    label: 'Governed In Your Environment',
    detail: 'Role-scoped access, documented handoffs, and protected implementation detail preserve control beyond the lead purchase.',
  },
];

const personas = [
  {
    title: 'Insurance & Agency Leadership',
    description: 'Trusted supply across book and territory. Reliable delivery, consistent standards, and producers working supply leadership can defend.',
    accent: 'indigo',
  },
  {
    title: 'Sales & Revenue Executives',
    description: 'Acquisition spend behind a supply chain, not a list purchase. Available pipeline with evidence for every budget conversation.',
    accent: 'emerald',
  },
  {
    title: 'Sales Management',
    description: 'Ranked queues that hold up week to week. Coaching built on consistent inputs, not explaining why this CAB differs from the last.',
    accent: 'amber',
  },
  {
    title: 'Agents & Producers',
    description: 'Contacts worth calling into — verified, ranked, with context attached. Supply you trust enough to build your day around.',
    accent: 'rose',
  },
];

const verticals = ['Insurance', 'P&C', 'Life & Health', 'Mortgage', 'B2B', 'Inside Sales', 'Field Teams'];

const trustPoints = [
  'Trusted lineage on every contact — source, score, and CAB delivery documented',
  'Reliable handoff to the systems your team already runs — CRM, dialer, work queue',
  'Consistent quality thresholds enforced on every Customer Acquisition Bundle before release',
  'Available on your territory cadence — configured to lines of business and growth targets',
];

const pains = [
  {
    title: 'Delivery Without Proof',
    line: '“Real time” and “high intent” describe speed and promise. They do not explain the score, evidence, or release standard.',
  },
  {
    title: 'Targeting Without Capacity',
    line: 'ZIPs, hours, filters, and bids control acquisition. They do not balance territory ownership, producer load, or monthly caps.',
  },
  {
    title: 'CRM Handoff Without Execution',
    line: 'A handoff into email or another system can still leave producers resolving records, switching tools, and reporting outcomes separately.',
  },
  {
    title: 'Analytics That Stop At Delivery',
    line: 'Volume and campaign reporting cannot govern spend unless reached, quoted, and bound outcomes return to the source decision.',
  },
];

const accentBorder = {
  indigo: 'border-l-indigo-500',
  emerald: 'border-l-emerald-500',
  amber: 'border-l-amber-500',
  rose: 'border-l-rose-400',
};

const accentText = {
  indigo: 'text-indigo-400',
  emerald: 'text-emerald-400',
  amber: 'text-amber-400',
  rose: 'text-rose-400',
};

const EigenLeads = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className={isDark ? 'bg-[#0a0c14] text-slate-100' : 'bg-white text-slate-800'}>
      <SEOHead
        title="eigenLeads — Governed Lead Supply And Producer Execution"
        description="Move from ranked opportunity to accountable outcome in one governed loop: explainable scoring, certified quality, compliance, capacity-aware routing, producer workflow, and source-level economics."
        type="website"
        image="/images/eigenleads-logo.svg"
        imageAlt="eigenLeads — Governed Lead Programs by Shared Oxygen"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isDark
              ? 'radial-gradient(ellipse 75% 45% at 105% -5%, rgba(99,102,241,0.22), transparent 55%), radial-gradient(ellipse 55% 38% at -8% 108%, rgba(16,185,129,0.14), transparent 50%), radial-gradient(circle at 72% 88%, rgba(245,158,11,0.08), transparent 35%)'
              : 'radial-gradient(ellipse 75% 45% at 105% -5%, rgba(99,102,241,0.12), transparent 55%), radial-gradient(ellipse 55% 38% at -8% 108%, rgba(16,185,129,0.08), transparent 50%)',
          }}
        />
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <Link
            to="/"
            className={`inline-flex items-center gap-2 text-sm font-medium mb-10 ${isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
          >
            <ArrowLeft size={16} aria-hidden />
            Shared Oxygen
          </Link>

          <div className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.35fr)] gap-10 lg:gap-12 items-center">
            <div className="space-y-6 max-w-xl">
              <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">eigenLeads</h1>

              <span className="inline-block text-xs font-bold tracking-wide px-4 py-2 rounded-full border border-indigo-500/45 bg-indigo-500/10 text-indigo-300">
                Customer Acquisition Bundle (CAB)
              </span>

              <h2 className="text-3xl lg:text-[2.35rem] font-bold leading-[1.12] tracking-tight">
                From Ranked Opportunity To Bound Outcome — One Governed Loop.
              </h2>

              <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                Most lead programs are sold around delivery. eigenLeads governs what comes before and after —
                Customer Acquisition Bundles with diversified supply, cited priority, certified quality,
                capacity-aware routing, producer execution, and outcome economics.
              </p>
              <p className={`text-base leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Built for buyers who need more than names, calls, filters, and campaign dashboards:
                an operating system that proves CAB quality and connects acquisition spend to field results.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/contact?subject=eigenLeads%20Program%20Review"
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold rounded-xl transition-colors"
                >
                  Schedule A Program Review
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact?subject=eigenLeads%20Executive%20Briefing"
                  className={`inline-flex items-center gap-2 px-8 py-4 font-semibold rounded-xl border transition-colors ${
                    isDark
                      ? 'border-slate-600 text-slate-200 hover:border-indigo-500 hover:text-indigo-300'
                      : 'border-slate-300 text-slate-700 hover:border-indigo-500 hover:text-indigo-700'
                  }`}
                >
                  Request A Live Executive Session
                </Link>
              </div>
            </div>

            <div className="w-full min-w-0">
              <EigenLeadsExecutiveBriefingPlayer isDark={isDark} variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* Pain points — contrast with eigenLeads pillars */}
      <section className={`py-16 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Where Lead Delivery Stops Short</p>
          <h3 className="text-2xl font-bold mb-8">A Fast Handoff Is Not A Closed-Loop System.</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pains.map((pain) => (
              <div
                key={pain.title}
                className={`p-6 rounded-xl ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-slate-50 border border-slate-200'}`}
              >
                <h3 className="font-bold text-lg mb-2">{pain.title}</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {pain.line}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform architecture — supports the briefing narrative */}
      <section className={`py-16 lg:py-20 ${isDark ? 'bg-slate-950/50' : 'bg-slate-50'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="space-y-5 max-w-lg">
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Platform Architecture</p>
              <h3 className="text-2xl lg:text-3xl font-bold leading-tight">
                Governed Supply From Source To Queue
              </h3>
              <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Diversified sourcing under one standard, evidence-backed quality gates, ranked delivery,
                capacity-aware assignment, and outcomes returned to the next allocation decision.
              </p>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                The briefing shows the producer experience. This is the governed operating model behind it.
              </p>
            </div>
            <EigenLeadsHeroDiagram theme={theme} />
          </div>
        </div>
      </section>

      {/* Four pillars — core differentiation */}
      <section className={`py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Why eigenLeads</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">Trust. Reliable. Consistent. Available.</h3>
          <p className={`max-w-2xl mb-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            The difference is not more rows. It is supply you can depend on — and contacts worth calling into.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {supplyChainPillars.map(({ icon: Icon, title, description, accent }) => (
              <div
                key={title}
                className={`p-6 rounded-xl border ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}
              >
                <Icon className={`w-6 h-6 mb-3 ${accentText[accent]}`} />
                <h4 className={`text-base font-bold mb-2 ${accentText[accent]}`}>{title}</h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality / scoring */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="space-y-5">
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Worth Calling Into</p>
              <h3 className="text-2xl lg:text-3xl font-bold leading-tight">
                Every Contact Ranked. Every Pack Certified.
              </h3>
              <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Producers receive a ranked queue they can trust — contact readiness, current signal context,
                compliance status, and EigenScore™ rationale on every record. QualityCert™ holds the release line.
              </p>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                The result is not merely a faster lead. It is a defensible reason to spend producer time,
                with attribution that survives through reached, quoted, and bound.
              </p>
            </div>
            <EigenLeadsQualityDiagram theme={theme} />
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className={`py-20 ${isDark ? 'bg-slate-900/40' : 'bg-slate-100'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">The Supply Chain</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">Operated End To End. Delivered On Standard.</h3>
          <p className={`max-w-2xl mb-10 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Shared Oxygen runs the chain from acquisition evidence through queue execution and outcome return —
            so your organization buys a measurable operating advantage, not another one-off file.
          </p>
          <EigenLeadsSupplyChainDiagram theme={theme} />
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">What Holds It Together</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-12">Built For Supply You Can Stand Behind</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentiators.map(({ icon: Icon, label, detail }) => (
              <div key={label} className="flex gap-4">
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                    isDark ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-50 text-indigo-600'
                  }`}
                >
                  <Icon size={20} />
                </div>
                <div>
                  <h4 className="font-bold mb-1">{label}</h4>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {detail}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personas */}
      <section className={`py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Audience</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-10">From P&L Ownership To Producer Execution</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {personas.map((persona) => (
              <div
                key={persona.title}
                className={`p-6 rounded-xl border-l-4 ${accentBorder[persona.accent]} ${
                  isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <h4 className="font-bold mb-2">{persona.title}</h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {persona.description}
                </p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-10">
            {verticals.map((v) => (
              <span
                key={v}
                className={`text-sm font-semibold px-4 py-1.5 rounded-full border ${
                  isDark ? 'border-white/10 text-slate-400 bg-white/[0.03]' : 'border-slate-200 text-slate-600 bg-white'
                }`}
              >
                {v}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / delivery */}
      <section className={`py-20 ${isDark ? 'bg-slate-950/80' : 'bg-slate-50'}`}>
        <div className="max-w-6xl mx-auto px-6 space-y-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Delivery Commitment</p>
              <h3 className="text-2xl lg:text-3xl font-bold mb-6">Trusted Supply, Reliably Released</h3>
              <p className={`mb-8 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                Every Customer Acquisition Bundle ships with documented evidence, defined ownership, a certified release,
                and a traceable handoff. Every outcome can return to the source and signal that produced it.
              </p>
              <ul className="space-y-4">
                {trustPoints.map((item) => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <span className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className={`p-8 rounded-2xl border space-y-6 ${
                isDark ? 'bg-slate-900/60 border-white/10' : 'bg-white border-slate-200 shadow-md'
              }`}
            >
              <div className="flex items-center gap-3">
                <Building2 className={`w-6 h-6 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                <span className="font-bold">Operated Inside Your Environment</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className={`w-6 h-6 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <span className="font-bold">Compliance Standards Your Counsel Can Review</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className={`w-6 h-6 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                <span className="font-bold">Territory To Queue — Available On Cadence</span>
              </div>
            </div>
          </div>
          <EigenLeadsDeliveryDiagram theme={theme} />
        </div>
      </section>

      {/* Footer CTA */}
      <section className={`py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <h3 className={`text-3xl lg:text-4xl font-bold leading-tight text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <span className="block">Buy More Than Lead Delivery.</span>
            <span className={`block mt-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
              Govern The Full Commercial Loop.
            </span>
          </h3>
          <p className={`text-lg max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Provenance, priority, quality, compliance, routing, producer execution, and CAB economics —
            modeled to your territories and operating constraints.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact?subject=eigenLeads%20Program%20Scope"
              className="inline-flex items-center gap-2 px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors"
            >
              Schedule A Conversation
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="#executive-briefing"
              className={`inline-flex items-center gap-2 px-8 py-4 font-semibold rounded-xl border transition-colors ${
                isDark
                  ? 'border-slate-600 text-slate-200 hover:border-indigo-500 hover:text-indigo-300'
                  : 'border-slate-300 text-slate-700 hover:border-indigo-500 hover:text-indigo-700'
              }`}
            >
              Replay Executive Briefing
            </a>
          </div>
          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            eigenLeads — A Product Of Shared Oxygen, LLC
          </p>
        </div>
      </section>
    </div>
  );
};

export default EigenLeads;
